import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {GetResult, QueryResult, RemitApiResponse} from '..';
import {QINVENTORY_BASE_URL} from './config';

export interface ProductExpiry {
  id: string;
  productId: string;
  quantity: number;
  expiryDate: string; // ISO 8601 format
  version: string;
  createdAt: string; // ISO 8601 format
  createdBy: string;
  updatedAt: string; // ISO 8601 format
  updatedBy: string;
}

export interface ProductExpiryResponse {
  id: string;
  productId: string;
  quantity: number;
  expiryDate: string; // ISO 8601 format
  version: string;
  createdAt: string; // ISO 8601 format
  createdBy: string;
  updatedAt: string; // ISO 8601 format
  updatedBy: string;
  sku: string;
  productName: string;
  image: string;
}

export interface ProductExpiryCreateRequest {
  sku: string;
  quantity: number;
  expiryDate: string; // ISO 8601 format, required
}

export interface ProductExpiryUpdateRequest {
  sku?: string;
  quantity?: number;
  expiryDate?: string; // ISO 8601 format
}

export interface ProductExpiryQueryParams {
  pageNumber: number;
  pageSize: number;
  sortBy: string;
  sortDirection: number; // 1 for ascending, -1 for descending
  isDeleted?: 'Y' | 'N';
  skus?: string[]; // Array of SKU strings for filtering
  expiryDateStart?: string; // ISO 8601 format for start date range
  expiryDateEnd?: string; // ISO 8601 format for end date range
}

@Injectable({
  providedIn: 'root',
})
export class RemitProductExpiryService {
  constructor(
    private http: HttpClient,
    @Inject(QINVENTORY_BASE_URL) private remitApiUrl: string,
  ) { }

  /**
   * Creates a new product expiry record
   * @param {ProductExpiryCreateRequest} data Product expiry creation data
   * @return {Observable<RemitApiResponse>} Observable with API response
   */
  createProductExpiry(data: ProductExpiryCreateRequest): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/product-expiry',
        data,
    );
  }

  /**
   * Retrieves a specific product expiry record with enhanced product information
   * @param {string} id Product expiry ID
   * @return {Observable<ProductExpiryResponse>} Observable with product expiry response
   */
  getProductExpiry(id: string): Observable<ProductExpiryResponse> {
    return this.http.get<GetResult<ProductExpiryResponse>>(
        this.remitApiUrl + '/product-expiry/' + id,
    ).pipe(map((v) => v.body));
  }

  /**
   * Updates an existing product expiry record
   * @param {string} id Product expiry ID
   * @param {ProductExpiryUpdateRequest} data Product expiry update data
   * @return {Observable<RemitApiResponse>} Observable with API response
   */
  updateProductExpiry(
      id: string,
      data: ProductExpiryUpdateRequest,
  ): Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + '/product-expiry/' + id,
        data,
    );
  }

  /**
   * Deletes a product expiry record
   * @param {string} id Product expiry ID
   * @return {Observable<RemitApiResponse>} Observable with API response
   */
  deleteProductExpiry(id: string): Observable<RemitApiResponse> {
    return this.http.delete<RemitApiResponse>(
        this.remitApiUrl + '/product-expiry/' + id,
    );
  }

  /**
   * Queries product expiry records with pagination, sorting, and filtering
   * @param {ProductExpiryQueryParams} params Query parameters
   * @return {Observable<QueryResult<ProductExpiryResponse>>} Observable with query result
   */
  queryProductExpiries(
      params: ProductExpiryQueryParams,
  ): Observable<QueryResult<ProductExpiryResponse>> {
    return this.http.post<QueryResult<ProductExpiryResponse>>(
        this.remitApiUrl + '/product-expiries',
        params,
    );
  }
}
